import { z } from 'zod';

import { getLogger } from 'app-components/utils/core';
import { filteredArraySchema } from 'app-components/utils/zodHelpers';

const log = getLogger('app-components/flux/credit-report/creditReportSchema');

// A missing, null or malformed field reads as absent, so the stored report falls back to its default.
const optional = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

// The score ingredients as qcs-creditscore-svc sends them. An ingredient's counts and amounts are always
// present; its dates and age labels may be missing or null.
const CreditUsageSchema = z.object({
  creditUtilizationPercentage: z.number(),
  creditUtilizationAmount: z.number(),
  creditLimit: z.number(),
});

const PaymentHistorySchema = z.object({
  onTimePaymentsPercentage: z.number(),
  latePayments: z.number(),
  totalPayments: z.number(),
  totalLatePayments: z.number(),
});

const AgeOfCreditHistorySchema = z.object({
  averageAgeOfCredit: z.number(),
  averageAgeOfCreditAge: optional(z.string()),
  newestCreditLine: optional(z.string()),
  newestCreditLineAge: optional(z.string()),
  oldestCreditLine: optional(z.string()),
  oldestCreditLineAge: optional(z.string()),
});

const TotalAccountsSchema = z.object({
  openAccounts: z.number(),
  closedAccounts: z.number(),
  totalAccounts: z.number(),
});

const CreditInquiriesSchema = z.object({
  creditInquiries: z.number(),
  newestInquiry: optional(z.string()),
  newestInquiryAge: optional(z.string()),
  oldestInquiry: optional(z.string()),
  oldestInquiryAge: optional(z.string()),
});

const DerogatoryRemarksSchema = z.object({
  totalDerogatoryRemarks: z.number(),
  totalPublicRecords: z.number(),
  collectionAccounts: z.number(),
});

const CreditScoreHistoryEntrySchema = z.object({
  score: z.number(),
  refreshDate: z.string(),
});

/**
 * Parses one ingredient on its own, so a malformed ingredient reads as missing instead of discarding the
 * report. Dropping a whole ingredient changes what the user sees, so it is logged: only the path and the
 * issue codes, never the values, which are the user's credit data.
 */
const ingredient = <T extends z.ZodTypeAny>(schema: T) =>
  z.unknown().transform((value, ctx): z.infer<T> | undefined => {
    if (value === null || value === undefined) return undefined;
    const result = schema.safeParse(value);
    if (result.success) return result.data;
    log.error('Credit report ingredient does not match the expected shape; treating it as missing', {
      path: ctx.path.join('.'),
      issues: result.error.issues.map(({ path, code }) => ({ path: path.join('.'), code })),
    });
    return undefined;
  });

// QCS sends an ingredient only when the report has data for it; older reports have no age of credit history.
const CreditReportAggregatesSchema = z.object({
  creditUsage: ingredient(CreditUsageSchema),
  paymentHistory: ingredient(PaymentHistorySchema),
  ageOfCreditHistory: ingredient(AgeOfCreditHistorySchema),
  totalAccounts: ingredient(TotalAccountsSchema),
  creditInquiries: ingredient(CreditInquiriesSchema),
  derogatoryRemarks: ingredient(DerogatoryRemarksSchema),
});

const CreditReportSchema = z.object({
  id: optional(z.union([z.string(), z.number().transform(String)])),
  aggregates: optional(CreditReportAggregatesSchema),
  providerCreditReportId: optional(z.string()),
  creditScore: optional(z.union([z.string(), z.number()])),
  refreshDate: optional(z.string()),
  nextRefreshDate: optional(z.string()),
  refreshStatus: optional(z.object({ status: optional(z.string()) })),
  datasetPlatformUsedInRegistration: optional(z.string()),
  creditScoreHistory: optional(filteredArraySchema(CreditScoreHistoryEntrySchema)),
});

export type CreditUsage = z.infer<typeof CreditUsageSchema>;
export type PaymentHistory = z.infer<typeof PaymentHistorySchema>;
export type AgeOfCreditHistory = z.infer<typeof AgeOfCreditHistorySchema>;
export type TotalAccounts = z.infer<typeof TotalAccountsSchema>;
export type CreditInquiries = z.infer<typeof CreditInquiriesSchema>;
export type DerogatoryRemarks = z.infer<typeof DerogatoryRemarksSchema>;
export type CreditReportAggregates = z.infer<typeof CreditReportAggregatesSchema>;
export type CreditScoreHistoryType = z.infer<typeof CreditScoreHistoryEntrySchema>;

/** Validates a credit report response body; undefined when it is not an object. */
export const parseCreditReport = (data: unknown): z.infer<typeof CreditReportSchema> | undefined => {
  const result = CreditReportSchema.safeParse(data);
  return result.success ? result.data : undefined;
};
